import axios from 'axios';
import { callApi } from '@/hooks/useFetch';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Lowercase-slugify an entity name for a download filename; falls back when empty. */
export function slugifyFilename(name: string | null | undefined, fallback: string): string {
  const slug = (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

export interface JobStatusResponse {
  job_id: string;
  status: string;
  progress: number;
  current_item: number;
  total_items: number;
  message: string;
  result?: Record<string, unknown>;
  errors: string[];
}

/** The filename the server asked for, from its Content-Disposition.
 *
 *  The server names a document after the entity it belongs to ("Grupo Alea -
 *  Project Brief (ES).pdf"), which is the name the user wants; a caller-supplied
 *  name is only a fallback for endpoints that send no header. Prefers the RFC
 *  5987 `filename*` so non-ASCII titles survive. */
function serverFilename(disposition: unknown): string | null {
  if (typeof disposition !== 'string') return null;
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // A malformed header is not worth failing a download over.
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : null;
}

export async function downloadPdfFromApi(endpoint: string, filename: string) {
  const response = await axios({
    url: `${API_URL}/api${endpoint}`,
    method: 'GET',
    responseType: 'blob',
    withCredentials: true,
  });
  // The server's content type wins: not every generated document is a PDF (the
  // project profile is a PNG), and hardcoding one mislabels the saved file.
  const blob = new Blob([response.data], {
    type: response.headers['content-type'] || 'application/pdf',
  });
  const name = serverFilename(response.headers['content-disposition']) || filename;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export interface WaitForJobOptions {
  /** Poll endpoint for callers whose session cannot reach the staff
   *  `/jobs/{id}` — the Gaia participant polls `/gaia/me/jobs/{id}` with the
   *  entrepreneur cookie. Same `JobStatusResponse` shape either way. */
  statusUrl?: string;
  intervalMs?: number;
  maxDurationMs?: number;
}

export async function waitForJob(
  jobId: string,
  pollingRef: React.MutableRefObject<NodeJS.Timeout | null>,
  { statusUrl, intervalMs = 3000, maxDurationMs = 10 * 60 * 1000 }: WaitForJobOptions = {}
): Promise<JobStatusResponse> {
  const startedAt = Date.now();
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        if (Date.now() - startedAt > maxDurationMs) {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          reject(new Error(`Job timed out after ${Math.round(maxDurationMs / 60000)} min (it may still be running in the background)`));
          return;
        }
        const status = await callApi<JobStatusResponse>(statusUrl ?? `/jobs/${jobId}`);
        if (status.status === 'failed' || status.status === 'error') {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          reject(new Error(status.errors?.[0] || status.message || 'Job failed'));
          return;
        }
        if (status.status === 'completed') {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          resolve(status);
        }
      } catch (error) {
        clearInterval(pollingRef.current!);
        pollingRef.current = null;
        reject(error);
      }
    };

    pollingRef.current = setInterval(poll, intervalMs);
    poll();
  });
}
